import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  FormLabel,
  ModalError,
  ModalSuccess,
  PasswordInput,
  NumberInput,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "components";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { getErrors, validateEmail } from "utils";

const ModalFormUserSociety = ({ open, onClose, preload, getData }) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingButton, setLoadingButton] = useState(false);
  const [optionRole, setOptionRole] = useState([]);
  const [optionSociety, setOptionSociety] = useState([]);
  const [passwordError, setPasswordError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [payload, setPayload] = useState({
    username: "",
    email: "",
    phone: "",
    role_id: "",
    society_id: "",
    ...(!preload && {
      password: "",
      confirm_password: "",
    }),
  });
  const buttonDisabled =
    !payload?.name ||
    !payload?.email ||
    !payload?.role_id ||
    !payload?.society_id ||
    (preload && (!payload?.confirm_password || !payload?.password));

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
    if (!preload && (key === "password" || key === "confirm_password")) {
      setPasswordError(null);
    }
    if (key === "email") {
      setEmailError(null);
    }
  };
  const getPreload = () => {
    setPayload(preload);
  };

  const getOptionRole = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/role`, {
        headers,
        params: {
          page: 1,
          per_page: -1,
          type: "society",
        },
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.role_id,
        label: item?.name,
      }));
      setOptionRole(modifiedData);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };
  const getOptionSociety = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/admin/society/`, {
        headers,
        params: {
          page: 1,
          per_page: -1,
          sort: "",
          search: "",
        },
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        label: item?.society_name,
      }));
      setOptionSociety(modifiedData);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };
  const handleSubmit = async () => {
    const method = preload ? axios.put : axios.post;
    const { password, confirm_password, email } = payload;
    let hasError = false;
    setPasswordError(null);
    setEmailError(null);

    if (!preload && password !== confirm_password) {
      setPasswordError("Password and Confirm Password does not match");
      hasError = true;
    }

    if (!validateEmail(email)) {
      setEmailError(
        "Invalid email, please enter your email address in format: yourname@example.com"
      );
      hasError = true;
    }

    if (hasError) return;

    try {
      setLoadingButton(true);
      await method(
        `${hardBaseUrl}/user${
          preload ? `/update/${preload?.user_id}` : "/register"
        }`,
        payload,
        { headers }
      );
      onClose();
      ModalSuccess(
        `Successfully ${preload ? "Update" : "Create"} User Society`
      ).then(() => {
        setPayload({});
        getData();
      });
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    getOptionRole();
    getOptionSociety();
  }, []);

  useEffect(() => {
    getPreload();
  }, [preload]);

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{`${preload ? "Edit" : "Add"} User Society`}</DialogTitle>
      <DialogContent dividers>
        <Grid container direction="column" rowSpacing={1}>
          {MockFieldForm({
            preload,
            passwordError,
            emailError,
            optionSociety,
            optionRole,
          })?.map(
            ({ title, key, type, error, options, optionLabel }, index) => (
              <Grid item key={index}>
                <FormLabel label={title} required />
                {type === "autocomplete" ? (
                  <AutoCompleteComponent
                    placeholder={title}
                    options={options}
                    value={
                      options?.find(
                        option => option.id === Number(payload?.[key])
                      ) || null
                    }
                    onChange={value => handleChangePayload(value, key)}
                    width="100%"
                    size="small"
                    renderOption={(props, option) => {
                      return (
                        <Box component="li" {...props} key={option?.[key]}>
                          {option?.[optionLabel]}
                        </Box>
                      );
                    }}
                  />
                ) : type === "password" ? (
                  <PasswordInput
                    placeholder={title}
                    autoComplete={`new-${key}`}
                    value={payload?.[key]}
                    onChange={e => handleChangePayload(e?.target?.value, key)}
                    error={!!error}
                    helperText={error}
                    disabled={loadingButton}
                  />
                ) : type === "phone" ? (
                  <NumberInput
                    placeholder={title}
                    autoComplete={`new-${key}`}
                    value={payload?.[key]}
                    onChange={e => handleChangePayload(e, key)}
                    disabled={loadingButton}
                  />
                ) : (
                  <TextInput
                    placeholder={title}
                    value={payload?.[key]}
                    autoComplete={`new-${key}`}
                    onChange={e => handleChangePayload(e?.target?.value, key)}
                    error={!!error}
                    helperText={error}
                    disabled={loadingButton}
                  />
                )}
              </Grid>
            )
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <SecondaryButton label="Cancel" onClick={onClose} />
        <PrimaryButton
          label={
            loadingButton
              ? preload
                ? "Updating"
                : "Submitting"
              : preload
              ? "Update"
              : "Add"
          }
          loading={loadingButton}
          disabled={buttonDisabled || loadingButton}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

const MockFieldForm = ({
  preload,
  passwordError,
  emailError,
  optionSociety,
  optionRole,
}) => [
  {
    title: "User Name",
    key: "username",
  },
  {
    title: "Email",
    key: "email",
    error: emailError,
  },
  {
    title: "Phone Number",
    key: "phone",
    type: "phone",
  },
  {
    title: "Society",
    key: "society_id",
    type: "autocomplete",
    options: optionSociety,
    optionLabel: "society_name",
  },
  {
    title: "Role",
    key: "role_id",
    type: "autocomplete",
    options: optionRole,
    optionLabel: "name",
  },
  ...(preload
    ? []
    : [
        {
          title: "Password",
          key: "password",
          type: "password",
          error: passwordError,
        },
        {
          title: "Confirm Password",
          key: "re_password",
          type: "password",
          error: passwordError,
        },
      ]),
];
export default ModalFormUserSociety;
